/* eslint-disable prefer-destructuring,func-names,prefer-arrow-callback */

import axios from 'axios';
import _ from 'lodash';
import spottingGroupSpecDataJSON from '../../../../API_MOCKS/definition/spotting/groups/index.json';
import SpottingGroup from '../SpottingGroupRecord';
import { MODEL_SPOTTING_GROUP_IDENTITY } from './spottingGroupConstants';

const API_URL = process.env.API_URL;
const NODE_ENV = process.env.NODE_ENV;

/**
 * these methods connect/simulate REST APIs. its up to Redux to call and sync with them.
 *
 * @param bottle {Bottlejs}
 */

export default (bottle) => {
  bottle.constant('spottingGroupDataURI', `${API_URL}/definition/spotting/groups`);

  bottle.constant('spottingGroupSpecData', spottingGroupSpecDataJSON);

  bottle.factory('headers', () => token => ({
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }));

  bottle.factory('spottingGroupModel', ({ spottingGroupSpecData, recordToID }) => {
    const model = {
      get data() {
        const out = _.cloneDeep(model._data);
        out.groups = out.groups.map(r => new SpottingGroup(r));
        return out;
      },
      _data: _.cloneDeep(spottingGroupSpecData),
      setGroups(data) {
        model._data.groups = data.map(record => new SpottingGroup(record));
      },
      get(name) {
        return _.find(model.data.groups, { name });
      },
      add(record) {
        const group = new SpottingGroup(record);
        if (!group.groupName) throw new Error('no name');
        model.removeIDs(group);
        model._data.groups = [...model.data.groups, group];
      },
      removeIDs(ids) {
        let list = Array.isArray(ids) ? ids : [ids];
        list = list.map(recordToID);
        model._data.groups = _.reject(model.data.groups, v => list.includes(v.groupName));
      },
    };
    return model;
  });

  bottle.factory('spottingGroupAddRecord', function ({
    recordToID, spottingGroupDataURI, headers, spottingGroupModel,
  }) {
    return async (record, token) => {
      const group = new SpottingGroup(record);

      if (!_.get(group, MODEL_SPOTTING_GROUP_IDENTITY)) {
        throw new Error(`data missing ${MODEL_SPOTTING_GROUP_IDENTITY}, ${record}`);
      }

      if (NODE_ENV === 'test') {
        return spottingGroupModel.add(group);
      }

      return axios.put(
        `${spottingGroupDataURI}/${encodeURIComponent(recordToID(record))}`,
        group.toJSON(),
        { headers: headers(token) },
      ).catch((err) => {
        throw _.get(err, 'response.data.errors[0]', err);
      });
    };
  });

  bottle.factory('recordToID', () => (record) => {
    if (record) {
      if (typeof record === 'string') {
        return record;
      }
      if (typeof record === 'object') {
        return _.get(record, MODEL_SPOTTING_GROUP_IDENTITY, null);
      }
    }
    return null;
  });

  bottle.factory('spottingGroupUpdateRecord', function ({
    recordToID, spottingGroupRemoveRecords, spottingGroupAddRecord,
  }) {
    return async (group, newRecord, token) => {
      const oldID = recordToID(group);
      const out = await spottingGroupAddRecord(newRecord, token);
      if (oldID !== newRecord.groupName) {
        return spottingGroupRemoveRecords(oldID, token);
      }
      return out;
    };
  });

  bottle.factory('spottingGroupFetchAll', function ({
    spottingGroupModel, headers, spottingGroupDataURI,
  }) {
    return async (token) => {
      if (NODE_ENV === 'test') {
        return spottingGroupModel.data;
      }

      const { data } = await axios.get(spottingGroupDataURI, { headers: headers(token) });

      return _.get(data, 'groups', []) || [];
    };
  });

  bottle.factory('spottingGroupRemoveRecords', function ({
    spottingGroupModel, spottingGroupDataURI, recordToID, headers,
  }) {
    return async (records, token) => {
      const list = Array.isArray(records) ? records : [records];
      const ids = _(list).map(recordToID)
        .filter(_.identity)
        .uniq()
        .compact()
        .value();

      if (NODE_ENV === 'test') {
        return spottingGroupModel.removeIDs(ids);
      }

      return await Promise.all(ids.map(id => axios.delete(
        `${spottingGroupDataURI}/${encodeURIComponent(id)}`,
        { headers: headers(token) },
      )));
    };
  });

  bottle.factory('spottingGroupFetchRecord', function ({
    spottingGroupModel, recordToID, spottingGroupDataURI, headers,
  }) {
    return async (recordOrName, token) => {
      const groupName = recordToID(recordOrName);
      if (!groupName) throw new Error(`cannot get ${recordOrName}: no name`);

      if (NODE_ENV === 'test') {
        return spottingGroupModel.get(groupName);
      }

      const { data } = await axios.get(
        `${spottingGroupDataURI}/${encodeURIComponent(groupName)}`,
        { headers: headers(token) },
      );
      return data;
    };
  });
};
